import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import * as S from './Variants.style';
import VariantSelector from '@modules/product/component/variant-selector/VariantSelector';
import VariantSelectorMobile from '@modules/product/component/variant-selector/VariantSelector.mobile';
import VariantPurchase from '@modules/product/component/variant-purchase/VariantPurchase';
import SamplePurchase from '@modules/product/component/sample-purchase/SamplePurchase';
import { useSampleStock } from '@modules/product/hook/use-sample-stock';
import VariantPricing from '@modules/product/component/variant-pricing/VariantPricing';
import useVariantPrice from '../../hook/use-variant-price';
import React from 'react';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import SpecialtyPricingBanner from '@modules/product/component/specialty-pricing-banner/SpecialtyPricingBanner';
import type Product from '@modules/product/type/product';
import { ItemShippingProvider } from '@modules/ecommerce/context/ItemShippingContext';
import TradeOnlyBanner from '../trade-only-banner/TradeOnlyBanner';
import { useUser } from '~/modules/user';
import { VariantShippingMethodsProvider } from '~/modules/ecommerce/context/VariantShippingMethodsContext';

interface VariantsProps {
    data: {
        product: Product;
        ecommerceEnabled: boolean;
    };
    whereToBuyEnabled?: boolean;
    onClickWhereToBuy?: () => void;
}

const Variants = ({
    data: { product, ecommerceEnabled },
    whereToBuyEnabled,
    onClickWhereToBuy,
}: VariantsProps) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const Selector = mobile ? VariantSelectorMobile : VariantSelector;
    const { hasPrice: hasVariantPrice } = useVariantPrice(
        product.Pricing.PricingType
    );
    const { selectedVariant, quantity } = useVariantSelectionState();
    const { canPurchase: sampleCanPurchase } = useSampleStock(selectedVariant);

    const sizesInUse = React.useMemo(
        () =>
            product.Sizes.filter(
                (s) =>
                    product.Variants.some((v) => v.Size?.ID === s.ID) ||
                    (product.Variants.length === 0 &&
                        product.MasterVariant?.Size?.ID === s.ID)
            ),
        [product]
    );
    const pricingType = product.Pricing.PricingType;
    const shouldShowPurchase = product.EcommerceReady;

    const hasVariantShippingOptions =
        (selectedVariant?.ShippingProfile?.ShippingMethods ?? []).length > 0 ||
        (product.Professional.PickupLocations.length ?? 0) > 0;

    const hasShippingMethods = selectedVariant?.SampleShippingProfile
        ?.NoShipping
        ? false
        : (selectedVariant?.SampleShippingProfile?.ShippingMethods ?? [])
              .length > 0;

    const pickupLocations = product.Professional.PickupLocations;

    const hasSampleShippingOptions =
        hasShippingMethods || pickupLocations.length > 0;

    const user = useUser();

    return (
        <VariantShippingMethodsProvider
            selectedVariant={selectedVariant}
            quantity={quantity}
            estimatedSampleWeight={product.Professional.SampleEstimatedWeight}
        >
            <ItemShippingProvider>
                <div className={css(S.VariantPurchaseGroup)}>
                    {mobile && ecommerceEnabled && (
                        <SpecialtyPricingBanner product={product} />
                    )}
                    <Selector
                        data={{
                            variants: product.Variants,
                            sizes: sizesInUse,
                        }}
                    />
                    {mobile && hasVariantPrice && (
                        <>
                            <VariantPricing
                                pricingType={product.Pricing.PricingType}
                            />
                            <TradeOnlyBanner
                                selectedVariant={selectedVariant}
                                user={user}
                            />
                        </>
                    )}
                    {shouldShowPurchase && (
                        <>
                            {pricingType === 'ecommercePricing' &&
                            hasVariantShippingOptions ? (
                                <VariantPurchase
                                    product={product}
                                    whereToBuyEnabled={whereToBuyEnabled}
                                    onClickWhereToBuy={onClickWhereToBuy}
                                />
                            ) : sampleCanPurchase &&
                              hasSampleShippingOptions ? (
                                <SamplePurchase
                                    productId={product.ID}
                                    whereToBuyEnabled={whereToBuyEnabled}
                                    onClickWhereToBuy={onClickWhereToBuy}
                                />
                            ) : null}
                        </>
                    )}
                </div>
            </ItemShippingProvider>
        </VariantShippingMethodsProvider>
    );
};

export default Variants;
